import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, Alert, Card, Switch, Divider } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { baseUrl_SPB } from '../../../BaseUrl';
import axios from 'axios';

const EMPLOYMENT_TYPES = [
  { label: 'Full-time', value: 'Fulltime' },
  { label: 'Part-time', value: 'Parttime' },
  { label: 'Casual', value: 'Casual' },
];

const Y_N_OPTIONS = [
  { label: 'Yes', value: 'Y' },
  { label: 'No', value: 'N' },
];

const EmploymentConditionsPanel = ({ employee, token, onUpdate }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    const applyFormValues = (source = {}) => {
      form.setFieldsValue({
        // Employment section
        employmentType: source.employmentType || 'Fulltime',
        annualSalary: source.annualSalary || '',
        maxHoursAllowed: source.maxHoursAllowed || '',

        // Timesheet & Fair Work
        timesheetRequiredForAgreedOrNormalHours: source.timesheetRequiredForAgreedOrNormalHours || 'Y',
        awardCode: source.userFairWorkDetails?.awardCode || '',
        fwClassificationLevel: source.userFairWorkDetails?.fwClassificationLevel || '',

        // Tax & Residency
        resident: source.resident || 'Y',
        helpDebt: source.helpDebt || 'N',
        tfthreshold: source.tfthreshold || 'Y',
      });
    };

    const userId = employee?.id || employee?.userId || employee?.employeeId;
    if (!employee) return;

    applyFormValues(employee);

    let isMounted = true;
    const fetchEmploymentConditions = async () => {
      if (!token || !userId) return;

      try {
        const response = await fetch(`${baseUrl_SPB}/api/v1/user/employmentconditions/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch employment conditions: ${response.status}`);
        }

        const data = await response.json();
        if (!isMounted || !data) return;

        applyFormValues({
          ...employee,
          ...data,
          userFairWorkDetails: data.userFairWorkDetails || employee.userFairWorkDetails,
        });
      } catch (err) {
        console.error('Failed to load employment conditions.', err);
      }
    };

    fetchEmploymentConditions();

    return () => {
      isMounted = false;
    };
  }, [employee, form, token]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      const payload = {
        ...employee,
        employmentType: values.employmentType,
        annualSalary: values.annualSalary,
        maxHoursAllowed: values.maxHoursAllowed,
        timesheetRequiredForAgreedOrNormalHours: values.timesheetRequiredForAgreedOrNormalHours,
        resident: values.resident,
        helpDebt: values.helpDebt,
        tfthreshold: values.tfthreshold,
        userFairWorkDetails: {
          awardCode: values.awardCode,
          fwClassificationLevel: values.fwClassificationLevel,
        },
      };

      await axios.post(`${baseUrl_SPB}/api/v1/user/employmentconditions/save`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setChanged(false);
      onUpdate?.();
    } catch (err) {
      if (err?.errorFields) return;
      setError('Failed to save employment conditions.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      
      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => setChanged(true)}
      >
        {/* Employment Section */}
        <Card title="Employment" size="small">
          <Form.Item
            name="employmentType"
            label="Employment type"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select options={EMPLOYMENT_TYPES} />
          </Form.Item>

          <Form.Item
            name="annualSalary"
            label="Annual salary"
          >
            <Input 
              type="number" 
              placeholder="Annual amount" 
              prefix="$"
            />
          </Form.Item>

          <Form.Item
            name="maxHoursAllowed"
            label="Maximum hours allowed per week"
          >
            <Input type="number" placeholder="e.g. 40" />
          </Form.Item>
        </Card>

        {/* Timesheet & Fair Work Section */}
        <Card title="Timesheet & Fair Work" size="small">
          <Form.Item
            name="timesheetRequiredForAgreedOrNormalHours"
            label="Timesheet required for agreed/normal hours"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select options={Y_N_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="awardCode"
            label="Award code"
          >
            <Input placeholder="e.g. MA000100" />
          </Form.Item>

          <Form.Item
            name="fwClassificationLevel"
            label="Fair Work classification level"
          >
            <Input placeholder="e.g. Level 1, Level 2" />
          </Form.Item>
        </Card>

        {/* Tax & Residency Section */}
        <Card title="Tax & Residency" size="small">
          <Form.Item
            name="resident"
            label="Resident"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select options={Y_N_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="helpDebt"
            label="HELP debt"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select 
              options={Y_N_OPTIONS}
              placeholder="Has outstanding HELP debt (student loan)"
            />
          </Form.Item>

          <Form.Item
            name="tfthreshold"
            label="Meets tax-free threshold"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select options={Y_N_OPTIONS} />
          </Form.Item>
        </Card>
      </Form>

      {changed && (
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          block
        >
          Save changes
        </Button>
      )}
    </Space>
  );
};

export default EmploymentConditionsPanel;
